import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import { Slice } from "@prismicio/types";
import React, { useRef, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import {
  TabbedContentSliceFragment,
  PrismicReusableContent,
  PrismicReusableContentDataBodyTextAndImage,
  PrismicReusableContentDataBodyTextAndVideo,
} from "../../types/graphql";
import { IPageContext } from "../../types/types";
import { htmlSerializer } from "../../utils/htmlSerializer";
import { SharedSliceType } from "../shared-slice/shared-slice";
import { GatsbyImage } from "gatsby-plugin-image";
import { youtubeRegEx } from "../../utils/constants";
import VisibilitySensor from "react-visibility-sensor";
import Youtube from "../youtube/youtube";

export type ITabbedContentProps = Slice<
  SharedSliceType,
  TabbedContentSliceFragment["primary"]
> & { type: SharedSliceType.tabbed_content };

const TabbedContent = ({
  slice,
}: SliceComponentProps<ITabbedContentProps, IPageContext>) => {
  const { primary } = slice;

  const document = primary?.tab_content?.document as PrismicReusableContent;
  const body = document?.data?.body as
    | PrismicReusableContentDataBodyTextAndImage[]
    | PrismicReusableContentDataBodyTextAndVideo[];

  // Slick Slider
  const sliderRef = useRef<Slider>(null);
  const [sliderInitialized, setSliderInitialized] = useState(false);
  const settings = {
    arrows: false,
    dots: true,
    dotsClass:
      "tabs lg:border-b-1 lg:!grid lg:!grid-cols-4 lg:!gap-4 [&>li]:text-center [&>li]:self-center [&>li]:h-full [&>li]:!grid [&>li]:!gridl-cols-1 [&>li]:content-center [&>.slick-active]:text-[#0E6AED] [&>li_a_h6]:hover:!text-[#0E6AED] [&>.slick-active]:border-b-4 [&>.slick-active]:border-[#0E6AED] [&>.slick-active_a_h6]:!text-[#0E6AED] [&>.slick-active_a]:pb-1",
    fade: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    waitForAnimate: false,
    customPaging: function (i: number) {
      return (
        <a className="uppercase font-semibold block py-2 px-2 cursor-pointer">
          <PrismicRichText
            field={body[i]?.primary?.heading.richText}
            components={htmlSerializer}
          />
        </a>
      );
    },
    onInit: () => setSliderInitialized(true),
  };

  const [hasViewed, setHasViewed] = useState(false);

  return (
    <section className="slice-section theme-light-blue">
      <VisibilitySensor
        onChange={(isVisible: boolean) => isVisible && setHasViewed(true)}
        offset={{ bottom: -1200 }}
      >
        <div className="container">
          <div className="text-center">
            <PrismicRichText
              field={slice.primary?.primary_heading?.richText}
              components={htmlSerializer}
            />
            {slice.primary?.primary_description && (
              <PrismicRichText
                field={slice.primary?.primary_description?.richText}
                components={htmlSerializer}
              />
            )}
          </div>
          <div
            className="relative"
            style={{ paddingBottom: settings.dots ? "25px" : "0" }}
          >
            <Slider
              ref={sliderRef}
              className="!flex !flex-col !flex-col-reverse [&>.slick-list]:!mt-16"
              {...settings}
            >
              {sliderInitialized &&
                body?.map((slide) => {
                  if (slide.slice_type === "text_and_image") {
                    const textAndImage =
                      slide as PrismicReusableContentDataBodyTextAndImage;
                    // TODO: Decouple to a component to accomodate variants
                    return (
                      <div className="!grid grid-cols-4 lg:!grid-cols-12 content-center !gap-8">
                        <div className="col-span-4 lg:!col-span-6 self-center">
                          <div className="[&_p]:text-lg [&_p]:text-xom-dark-75">
                            <PrismicRichText
                              field={textAndImage.primary.description.richText}
                              components={htmlSerializer}
                            />
                          </div>
                        </div>
                        <div className="col-span-4 lg:!col-span-6 rounded-xl overflow-hidden">
                          <GatsbyImage
                            image={
                              textAndImage?.primary?.image?.gatsbyImageData
                            }
                            alt={
                              textAndImage?.primary?.image?.alt ||
                              "Xometry Resources"
                            }
                            loading="lazy"
                          />
                        </div>
                      </div>
                    );
                  }
                  if (slide.slice_type === "text_and_video") {
                    const textAndVideo =
                      slide as PrismicReusableContentDataBodyTextAndVideo;
                    const youtubeRegexMatch =
                      textAndVideo?.primary?.video_url?.embed_url?.match(
                        youtubeRegEx
                      );
                    return (
                      <div className="!grid grid-cols-4 lg:!grid-cols-12 content-center !gap-8">
                        <div className="col-span-4 lg:!col-span-6 self-center">
                          <div className="[&_p]:text-lg [&_p]:text-[#476175]">
                            <PrismicRichText
                              field={textAndVideo.primary.description.richText}
                              components={htmlSerializer}
                            />
                          </div>
                        </div>
                        {textAndVideo?.primary?.video_url && (
                          <div className="col-span-4 lg:!col-span-6 rounded-xl overflow-hidden">
                            {(youtubeRegexMatch &&
                              youtubeRegexMatch.length > 0 &&
                              hasViewed && (
                                <Youtube
                                  embedURL={
                                    textAndVideo?.primary?.video_url
                                      ?.embed_url || ""
                                  }
                                  thumbnailURL={
                                    textAndVideo?.primary?.video_url
                                      ?.thumbnail_url || ""
                                  }
                                  title={
                                    textAndVideo?.primary?.video_url?.title ||
                                    ""
                                  }
                                />
                              )) ||
                              (textAndVideo?.primary?.video_url?.embed_url &&
                                hasViewed && (
                                  <iframe
                                    className="border-none w-full h-full md:h-[400px]"
                                    src={
                                      textAndVideo?.primary?.video_url
                                        ?.embed_url
                                    }
                                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                  />
                                ))}
                          </div>
                        )}
                      </div>
                    );
                  }
                })}
            </Slider>
          </div>
        </div>
      </VisibilitySensor>
    </section>
  );
};
export default TabbedContent;
